import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Shield, UserCog, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AdminTabs } from "../../components/admin/AdminTabs";
import { useConfirm } from "../../components/ConfirmProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const input = "w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand bg-white";

export default function AdminSubadmins() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", permissions: [] });
  const [saving, setSaving] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [uq, setUq] = useState("");
  const [selUser, setSelUser] = useState(null);
  const [roleForm, setRoleForm] = useState({ role: "subadmin", permissions: [] });
  const [savingRole, setSavingRole] = useState(false);

  const load = () => {
    api.get("/admin/subadmins").then(({ data }) => setList(data || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/available-permissions").then(({ data }) => setPerms(Array.from(new Set([...(data || []), "delete_accounts"])))).catch(() => setPerms(["delete_accounts"]));
  };
  useEffect(() => { load(); }, []);

  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/subadmins", form);
      toast.success(t("admin.subadmins.created"));
      setOpen(false); setForm({ email: "", password: "", name: "", permissions: [] }); load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : t("common.error"));
    } finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!(await confirm({ description: `${t("confirm.deleteDesc")}` }))) return;
    try { await api.delete(`/admin/subadmins/${s.id}`); toast.success(t("admin.subadmins.deleted")); setList((prev) => prev.filter((x) => x.id !== s.id)); }
    catch (err) { toast.error(t("common.error")); }
  };

  const openRole = () => {
    setSelUser(null); setUq(""); setRoleForm({ role: "subadmin", permissions: [] }); setRoleOpen(true);
    api.get("/admin/users").then(({ data }) => setAllUsers(data || [])).catch(() => {});
  };
  const toggleRolePerm = (p) => setRoleForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  const applyRole = async () => {
    if (!selUser) { toast.error(t("admin.subadmins.pickUserFirst")); return; }
    setSavingRole(true);
    try {
      await api.put(`/admin/users/${selUser.id}/role`, { role: roleForm.role, permissions: roleForm.role === "subadmin" ? roleForm.permissions : [] });
      toast.success(t("admin.subadmins.roleUpdated"));
      setRoleOpen(false); load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 405) toast.error(t("admin.subadmins.roleNotDeployed"));
      else { const msg = err?.response?.data?.detail; toast.error(typeof msg === "string" ? msg : t("common.error")); }
    } finally { setSavingRole(false); }
  };
  const filteredUsers = allUsers.filter((u) => {
    const s = uq.trim().toLowerCase();
    return !s || `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(s);
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-6">{t("admin.subadmins.title")}</h1>
      <AdminTabs />

      <div className="flex flex-wrap gap-3 mb-6">
        <button data-testid="subadmin-add-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors">
          <Plus className="w-4 h-4" /> {t("admin.subadmins.add")}
        </button>
        <button data-testid="change-role-btn" onClick={openRole} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-stone-border text-stone-sub font-semibold hover:border-brand hover:text-brand transition-colors">
          <UserCog className="w-4 h-4" /> {t("admin.subadmins.changeRole")}
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : list.length === 0 ? (
        <div className="py-12 text-center text-stone-muted" data-testid="subadmins-empty">{t("admin.subadmins.empty")}</div>
      ) : (
        <div className="space-y-3" data-testid="subadmins-list">
          {list.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-stone-border shadow-sm p-4 flex items-center justify-between gap-3" data-testid="subadmin-item">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center shrink-0"><Shield className="w-5 h-5 text-brand" /></div>
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-stone-text truncate">{s.name}</h3>
                  <p className="text-sm text-stone-muted truncate">{s.email}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(s.permissions || []).map((p) => (
                      <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-bg text-stone-sub">{t(`admin.permissions.${p}`, p)}</span>
                    ))}
                  </div>
                </div>
              </div>
              <button data-testid="subadmin-delete-btn" onClick={() => remove(s)} className="p-2 rounded-lg hover:bg-red-50 text-stone-muted hover:text-red-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="subadmin-modal">
          <DialogHeader><DialogTitle className="font-heading">{t("admin.subadmins.add")}</DialogTitle><DialogDescription>{t("admin.subadmins.permissions")}</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <input data-testid="subadmin-name" required placeholder={t("admin.subadmins.name")} className={input} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <input data-testid="subadmin-email" type="email" required placeholder={t("admin.subadmins.email")} className={input} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            <input data-testid="subadmin-password" type="password" required placeholder={t("admin.subadmins.password")} className={input} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
            <div>
              <p className="text-sm font-medium text-stone-sub mb-2">{t("admin.subadmins.permissions")}</p>
              <div className="grid grid-cols-2 gap-2">
                {perms.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm text-stone-sub bg-stone-bg rounded-lg px-3 py-2 cursor-pointer">
                    <input data-testid={`subadmin-perm-${p}`} type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} className="w-4 h-4 accent-[#D95A41]" />
                    {t(`admin.permissions.${p}`, p)}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <button type="submit" data-testid="subadmin-save" disabled={saving} className="px-5 py-2.5 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover disabled:opacity-60">{saving ? "..." : t("admin.subadmins.save")}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={roleOpen} onOpenChange={setRoleOpen}>
        <DialogContent data-testid="change-role-modal" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("admin.subadmins.changeRole")}</DialogTitle>
            <DialogDescription>{t("admin.subadmins.changeRoleDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-muted" />
              <input data-testid="role-user-search" value={uq} onChange={(e) => setUq(e.target.value)} placeholder={t("admin.subadmins.selectUser")} className={input + " pl-10"} />
            </div>
            <div className="max-h-48 overflow-y-auto border border-stone-border rounded-xl divide-y divide-stone-border/60" data-testid="role-user-list">
              {filteredUsers.length === 0 ? (
                <div className="p-4 text-sm text-stone-muted text-center">{t("admin.subadmins.noUsers")}</div>
              ) : filteredUsers.slice(0, 40).map((u) => (
                <button
                  key={u.id}
                  type="button"
                  data-testid="role-user-option"
                  onClick={() => setSelUser(u)}
                  className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 transition-colors ${selUser?.id === u.id ? "bg-brand-soft" : "hover:bg-stone-bg"}`}
                >
                  <span className="min-w-0">
                    <span className="block font-medium text-stone-text truncate">{u.name}</span>
                    <span className="block text-xs text-stone-muted truncate">{u.email}</span>
                  </span>
                  <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-stone-bg text-stone-sub shrink-0">{u.role}</span>
                </button>
              ))}
            </div>

            {selUser && (
              <div className="space-y-3" data-testid="role-config">
                <div>
                  <label className="block text-sm font-medium text-stone-sub mb-1">{t("admin.subadmins.roleLabel")}</label>
                  <select data-testid="role-select" className={input} value={roleForm.role} onChange={(e) => setRoleForm((f) => ({ ...f, role: e.target.value }))}>
                    <option value="candidate">{t("admin.subadmins.roleCandidate")}</option>
                    <option value="employer">{t("admin.subadmins.roleEmployer")}</option>
                    <option value="subadmin">{t("admin.subadmins.roleSubadmin")}</option>
                  </select>
                </div>
                {roleForm.role === "subadmin" && (
                  <div>
                    <p className="text-sm font-medium text-stone-sub mb-2">{t("admin.subadmins.permissions")}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {perms.map((p) => (
                        <label key={p} className="flex items-center gap-2 text-sm text-stone-sub bg-stone-bg rounded-lg px-3 py-2 cursor-pointer">
                          <input data-testid={`role-perm-${p}`} type="checkbox" checked={roleForm.permissions.includes(p)} onChange={() => toggleRolePerm(p)} className="w-4 h-4 accent-[#D95A41]" />
                          {t(`admin.permissions.${p}`, p)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button type="button" data-testid="role-apply" onClick={applyRole} disabled={savingRole || !selUser} className="px-5 py-2.5 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover disabled:opacity-60">
              {savingRole ? "..." : t("admin.subadmins.applyRole")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
